import { useCallback, useContext, useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { safeParse } from 'utils';
import { getIsEditorValueEmpty } from 'components/RichTextEditorField/utils';
import { CreateDataRoomShareableLinkForm } from 'models/ShareableLink';
import { DataRoomTermsAndConditions } from 'router/subrouters/DataRoom/models/DataRoomTermsAndConditions';
import TermsAndConditionsContext from 'router/subrouters/DataRoom/providers/TermsAndConditions/TermsAndConditions.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

export const INCLUDE_T_AND_C_NAME = 'includeTermsAndConditionProtection';
export const T_AND_C_NAME = 'termsAndConditionsContent';

const getHasContent = (content?: string) => {
  if (!content) return false;

  const parsed = safeParse(content);

  return !!parsed && !getIsEditorValueEmpty(parsed);
};

export default function useTermsAndConditionsTemplate() {
  const [selectedTemplate, setSelectedTemplate] =
    useState<DataRoomTermsAndConditions | null>(null);
  const [isNamingTemplate, setIsNamingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { addNewTermsAndConditions, updateExistingTermsAndConditions } =
    useContext(TermsAndConditionsContext);
  const { popSuccess } = useContext(PopNotificationsContext);

  const { change } = useForm();
  const {
    values: { termsAndConditionsContent: content },
  } = useFormState<CreateDataRoomShareableLinkForm>();

  const { t } = useTranslation();

  const hasContent = useMemo(() => getHasContent(content), [content]);

  const isContentChanged = useMemo(
    () => (selectedTemplate ? content !== selectedTemplate.content : false),
    [selectedTemplate, content],
  );

  const statusLabel = useMemo(() => {
    if (!selectedTemplate) return hasContent ? t('customTC') : null;

    return isContentChanged ? t('editedFromSavedTemplate') : null;
  }, [selectedTemplate, hasContent, isContentChanged, t]);

  const showSaveTemplate = useMemo(() => {
    if (!selectedTemplate) return hasContent;

    return isContentChanged;
  }, [selectedTemplate, hasContent, isContentChanged]);

  const onSelectTemplate = useCallback(
    (item: DataRoomTermsAndConditions) => {
      setSelectedTemplate(item);
      change(T_AND_C_NAME, item.content);
    },
    [change],
  );

  const onStartNaming = useCallback(() => {
    setTemplateName(selectedTemplate?.name ?? '');
    setIsNamingTemplate(true);
  }, [selectedTemplate]);

  const onCancelNaming = useCallback(() => {
    setIsNamingTemplate(false);
  }, []);

  const onSaveTemplate = useCallback(async () => {
    const name = templateName.trim();

    if (!name || isSaving) return;

    const shouldUpdate = !!selectedTemplate && isContentChanged;
    const payload = { name, content };

    try {
      setIsSaving(true);

      const result = shouldUpdate
        ? await updateExistingTermsAndConditions(selectedTemplate._id, payload)
        : await addNewTermsAndConditions(payload);

      if (!result) return;

      setSelectedTemplate(result);
      setIsNamingTemplate(false);
      popSuccess(t(shouldUpdate ? 'templateUpdated' : 'templateCreated'));
    } finally {
      setIsSaving(false);
    }
  }, [
    templateName,
    isSaving,
    selectedTemplate,
    isContentChanged,
    content,
    updateExistingTermsAndConditions,
    addNewTermsAndConditions,
    popSuccess,
    t,
  ]);

  return {
    selectedTemplate,
    statusLabel,
    showSaveTemplate,
    isNamingTemplate,
    templateName,
    isSaving,
    setTemplateName,
    onSelectTemplate,
    onStartNaming,
    onCancelNaming,
    onSaveTemplate,
  };
}
